/**
 * App Component
 *
 * Main application component with routing
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { NavBar } from './components/NavBar';
import { NotificationContainer } from './components/NotificationContainer';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { WelcomePage } from './pages/WelcomePage';
import { HomePage } from './pages/HomePage';
import { SmartRedirect } from './components/auth/SmartRedirect';
import { PromptBuilder } from './components/PromptBuilder';
import { GenerationPage } from './pages/GenerationPage';
import { ViewAngleTestPage } from './pages/ViewAngleTestPage';
import { BattleLobbyPage } from './pages/BattleLobbyPage';
import { DeploymentGridDemoPage } from './pages/DeploymentGridDemoPage';
import CreatureAnimationStudio from './pages/CreatureAnimationStudio';
import { CreatureGalleryPage } from './pages/CreatureGalleryPage';
import { CreatureDetailPage } from './pages/CreatureDetailPage';
import { AdminBetaPage } from './pages/AdminBetaPage';
import { DevEffectViewerPage } from './pages/DevEffectViewerPage';
import { AboutPage } from './pages/AboutPage';
import { InviteNotificationBanner } from './components/InviteNotificationBanner';
import { ChallengeHubPage } from './pages/ChallengeHubPage';
import { ChallengeDayPage } from './pages/ChallengeDayPage';
import BattlefieldEditorPage from './pages/BattlefieldEditorPage';
import { AdventureHubPage } from './pages/AdventureHubPage';
import { AdventureLobbyPage } from './pages/AdventureLobbyPage';
import { AdventureMapPage } from './pages/AdventureMapPage';
import { CoOpBattlePage } from './pages/CoOpBattlePage';
import { FeatureErrorBoundary } from './components/ErrorBoundary';
import { HexMapMockup } from './pages/HexMapMockup';

export const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationContainer />
        <InviteNotificationBanner />
        <div className="app">
          <NavBar />
          <Routes>
            {/* Auth routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/welcome" element={<WelcomePage />} />

            {/* Public marketing pages */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/dev/hex-map" element={<HexMapMockup />} />

            {/* Root - smart redirect based on auth state */}
            <Route path="/" element={<SmartRedirect />} />

            {/* Protected home page */}
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />

            {/* Public routes */}
            <Route path="/create" element={<PromptBuilder />} />
            <Route path="/generation/:jobId" element={<GenerationPage />} />
            {import.meta.env.DEV && (
              <>
                <Route path="/test-view-angles" element={<ViewAngleTestPage />} />
                <Route path="/animation-studio" element={<CreatureAnimationStudio />} />
              </>
            )}

            {/* Protected routes - require authentication */}
            <Route
              path="/gallery"
              element={
                <ProtectedRoute>
                  <CreatureGalleryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/creatures/:creatureId"
              element={
                <ProtectedRoute>
                  <CreatureDetailPage />
                </ProtectedRoute>
              }
            />
            {/* Battle Lobby - Central hub for finding/creating battles */}
            <Route
              path="/battles"
              element={
                <ProtectedRoute>
                  <FeatureErrorBoundary label="Battle Lobby">
                    <BattleLobbyPage />
                  </FeatureErrorBoundary>
                </ProtectedRoute>
              }
            />
            {/* Deployment Grid - Accessed via Battle Lobby with matchId/playerId params */}
            <Route
              path="/deployment"
              element={
                <ProtectedRoute>
                  <FeatureErrorBoundary label="Deployment">
                    <DeploymentGridDemoPage />
                  </FeatureErrorBoundary>
                </ProtectedRoute>
              }
            />
            {/* Admin routes */}
            <Route
              path="/admin/beta"
              element={
                <ProtectedRoute>
                  <AdminBetaPage />
                </ProtectedRoute>
              }
            />

            {/* Dev routes - only available in development builds */}
            {import.meta.env.DEV && (
              <>
                <Route path="/editor" element={<BattlefieldEditorPage />} />
                <Route
                  path="/dev/effects"
                  element={
                    <ProtectedRoute>
                      <DevEffectViewerPage />
                    </ProtectedRoute>
                  }
                />
              </>
            )}

            {/* Challenge routes */}
            <Route
              path="/challenges"
              element={
                <ProtectedRoute>
                  <ChallengeHubPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/challenges/day/:day"
              element={
                <ProtectedRoute>
                  <FeatureErrorBoundary label="Challenge Battle">
                    <ChallengeDayPage />
                  </FeatureErrorBoundary>
                </ProtectedRoute>
              }
            />

            {/* Adventure co-op routes */}
            <Route
              path="/adventure"
              element={
                <ProtectedRoute>
                  <AdventureHubPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/adventure/lobby/:adventureId"
              element={
                <ProtectedRoute>
                  <FeatureErrorBoundary label="Adventure Lobby">
                    <AdventureLobbyPage />
                  </FeatureErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/adventure/map/:adventureId"
              element={
                <ProtectedRoute>
                  <FeatureErrorBoundary label="Adventure Map">
                    <AdventureMapPage />
                  </FeatureErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/adventure/battle/:adventureId/:encounterId"
              element={
                <ProtectedRoute>
                  <FeatureErrorBoundary label="Co-Op Battle">
                    <CoOpBattlePage />
                  </FeatureErrorBoundary>
                </ProtectedRoute>
              }
            />

            {/* 404 fallback */}
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
};
